import React, { useEffect, useState } from 'react'
import { createAttendance } from '../../../services/AttendanceService'
import { getEmployeeTimeOutWithToken } from '../../../services/EmployeeService'
import LoadingDialog from '../../../helper/LoadingDialog'
import ResponseDialog from '../../../helper/ResponseDialog'
import ConfirmationDialog from '../clock-in-out/ConfirmationDialog'
import QRScannerModal from '../qr-scanner/QRScannerModal'

const Transaction = {
    TIME_IN: 1,
    TIME_OUT: 2
}

const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
const formatDate = (date) =>
    date.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

const AttendanceActions = () => {
    const [selectedLocation, setSelectedLocation] = useState('HOME')
    const [currentTime, setCurrentTime] = useState(new Date())
    const [loading, setLoading] = useState(false)
    const [responseMessage, setResponseMessage] = useState('')
    const [showResponseDialog, setShowResponseDialog] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [overtimeMessage, setOvertimeMessage] = useState('')
    const [currentTransaction, setCurrentTransaction] = useState(null)
    const [qrCode, setQRCode] = useState(null)
    const [isQrExempted, setIsQrExempted] = useState(false)

    // Offsite details
    const [offsiteLocation, setOffsiteLocation] = useState('')
    const [offsiteTasks, setOffsiteTasks] = useState('')
    const [startTime, setStartTime] = useState('')
    const [endTime, setEndTime] = useState('')

    const locationWorkingAtMap = {
        HOME: 1,
        OFFICE: 2,
        OFFSITE: 3
    }
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000)

        const checkQRExemption = () => {
            const qrExempt = localStorage.getItem('isQrExempted')
            // Convert to boolean: true if "1", false otherwise
            console.log('isQrExempted', localStorage.getItem('isQRExempted'))
            setIsQrExempted(qrExempt === '1')
            console.log('isQrExempted', qrExempt)
        }
        checkQRExemption()
        return () => clearInterval(timer)
    }, [])

    const handleLocationChange = (event) => setSelectedLocation(event.target.value)

    const handleClockAction = (transaction) => {
        setCurrentTransaction(transaction)
        console.log('location', locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE)
        if (locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE || isQrExempted) {
            if (transaction === Transaction.TIME_IN) {
                recordAttendance(Transaction.TIME_IN, null)
            } else {
                checkOvertime(null)
            }
        }
    }

    const handleAttendanceWithQR = (qrData) => {
        setQRCode(qrData)
        setLoading(true)

        if (currentTransaction === Transaction.TIME_IN) {
            recordAttendance(Transaction.TIME_IN, qrData)
        } else {
            checkOvertime(qrData)
        }
    }

    const recordAttendance = async (transaction, qrcode, reason = '') => {
        const workingAt = locationWorkingAtMap[selectedLocation] || 0

        if (
            workingAt === locationWorkingAtMap.OFFSITE &&
            (!startTime || !endTime || !offsiteLocation.trim() || !offsiteTasks.trim())
        ) {
            setResponseMessage(
                'Please complete all required offsite details: Start Time, End Time, Location, and Purpose.'
            )
            setShowResponseDialog(true)
            setLoading(false)
            setCurrentTransaction(null)
            return
        }

        const attendance = {
            transaction,
            workingAt,
            remarks: workingAt === locationWorkingAtMap.OFFSITE ? offsiteTasks : '',
            qrcode,
            isQrExempted: isQrExempted || locationWorkingAtMap[selectedLocation] === locationWorkingAtMap.OFFSITE,
            isOvertime: transaction === Transaction.TIME_OUT && reason !== '',
            reason,
            ...(workingAt === locationWorkingAtMap.OFFSITE && {
                offsiteDetails: {
                    timeStart: startTime,
                    timeEnd: endTime,
                    location: offsiteLocation,
                    purpose: offsiteTasks
                }
            })
        }

        await submitAttendance(attendance)
    }

    const submitAttendance = async (attendance) => {
        setLoading(true)
        try {
            const result = await createAttendance(attendance)
            setResponseMessage(result.error || result.message)
        } catch (error) {
            console.error('Attendance submission error:', error)
            setResponseMessage('An error occurred while recording attendance.')
        } finally {
            setLoading(false)
            setShowResponseDialog(true)
            setCurrentTransaction(null)
        }
    }

    const checkOvertime = async (qrData) => {
        try {
            const result = await getEmployeeTimeOutWithToken()
            const timeOut = parseInt(result.timeOut, 10)
            const defaultTimeOut = new Date().setHours(timeOut, 0, 0, 0)
            const gracePeriod = 60 * 60 * 1000 // 1hr

            if (new Date() > defaultTimeOut + gracePeriod) {
                const overtimeDuration = Math.floor((new Date() - defaultTimeOut) / (1000 * 60))
                const overtimeHours = Math.floor(overtimeDuration / 60)
                const overtimeMinutes = overtimeDuration % 60

                setOvertimeMessage(
                    `Please confirm: Do you have ${overtimeHours} hour(s) and ${overtimeMinutes} minute(s) of overtime? If yes, please enter the reason.`
                )
                setIsModalOpen(true)
            } else {
                recordAttendance(Transaction.TIME_OUT, qrData)
            }
        } catch (error) {
            console.error('Overtime check error:', error)
            setResponseMessage('An error occurred while checking overtime.')
        } finally {
            setLoading(false)
            setCurrentTransaction(null)
        }
    }

    const handleConfirm = (reason) => {
        setIsModalOpen(false)
        recordAttendance(Transaction.TIME_OUT, qrCode, reason)
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        recordAttendance(Transaction.TIME_OUT, qrCode)
    }

    return (
        <div className="flex flex-col h-full overflow-auto bg-white rounded-lg shadow-md p-6">
            <div className="flex-1 min-w-[320px]">
                {/* Title */}
                <h2 className="text-3xl font-bold text-gray-800 mb-6">Clock In / Out</h2>

                {/* Time Display */}
                <div className="text-center mb-6">
                    <p className="text-lg font-medium text-gray-600">{formatDate(currentTime)}</p>
                    <h3 className="text-6xl font-extrabold text-gray-900 mt-2">{formatTime(currentTime)}</h3>
                </div>

                {/* Location Selector */}
                <div className="flex justify-center space-x-4 mb-6">
                    {['HOME', 'OFFICE', 'OFFSITE'].map((location) => (
                        <label
                            key={location}
                            className={`flex items-center px-5 py-2 rounded-full cursor-pointer transition-all duration-200 border shadow-sm ${
                                selectedLocation === location
                                    ? location === 'HOME'
                                        ? 'bg-red-100 text-red-800 border-red-300'
                                        : location === 'OFFICE'
                                          ? 'bg-green-100 text-green-800 border-green-300'
                                          : 'bg-blue-100 text-blue-800 border-blue-300'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                            }`}
                        >
                            <input
                                type="radio"
                                value={location}
                                checked={selectedLocation === location}
                                onChange={handleLocationChange}
                                className="hidden"
                            />
                            {location}
                        </label>
                    ))}
                </div>

                {/* Offsite Details */}
                {selectedLocation === 'OFFSITE' && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 border">
                        <h4 className="text-xl font-semibold text-gray-700 mb-4">Offsite Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Start Time *</label>
                                <input
                                    type="time"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    className={`w-full p-2 rounded border ${
                                        !startTime ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">End Time *</label>
                                <input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    className={`w-full p-2 rounded border ${
                                        !endTime ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">Location *</label>
                                <input
                                    type="text"
                                    value={offsiteLocation}
                                    onChange={(e) => setOffsiteLocation(e.target.value)}
                                    placeholder="e.g., XYZ Corp, Taguig"
                                    className={`w-full p-2 rounded border ${
                                        !offsiteLocation.trim() ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Tasks / Purpose *
                                </label>
                                <textarea
                                    value={offsiteTasks}
                                    onChange={(e) => setOffsiteTasks(e.target.value)}
                                    placeholder="e.g., Meeting with client"
                                    rows={3}
                                    className={`w-full p-2 rounded border ${
                                        !offsiteTasks.trim() ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col md:flex-row justify-center items-center gap-4 mt-6 mb-4">
                    <button
                        onClick={() => handleClockAction(Transaction.TIME_IN)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold py-2 px-6 rounded w-full md:w-40 shadow"
                    >
                        Time In
                    </button>
                    <button
                        onClick={() => handleClockAction(Transaction.TIME_OUT)}
                        className="bg-red-600 hover:bg-red-700 text-white text-lg font-semibold py-2 px-6 rounded w-full md:w-40 shadow"
                    >
                        Time Out
                    </button>
                </div>

                {/* QR Exemption Note */}
                {isQrExempted && (
                    <p className="text-center text-green-700 font-medium text-sm mt-2">
                        QR scan is not required for your account.
                    </p>
                )}

                {/* Dialogs */}
                <LoadingDialog loading={loading} />
                <ResponseDialog
                    open={showResponseDialog}
                    onClose={() => setShowResponseDialog(false)}
                    responseMessage={responseMessage}
                />
                <ConfirmationDialog
                    isOpen={isModalOpen}
                    message={overtimeMessage}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
                {locationWorkingAtMap[selectedLocation] !== locationWorkingAtMap.OFFSITE &&
                    !isQrExempted &&
                    currentTransaction && (
                        <QRScannerModal onCancel={() => setCurrentTransaction(null)} onScan={handleAttendanceWithQR} />
                    )}
            </div>
        </div>
    )
}

export default AttendanceActions
